/**
 * Banco de Horas — saldo e extrato por funcionário.
 * GESTOR_RH pode lançar créditos/débitos e fazer ajustes manuais.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { bancoHorasApi, funcApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import { formatCpf } from '../../types/api'

const TIPO_LABELS: Record<string, { label: string; cor: string }> = {
  CREDITO:     { label: 'Crédito',     cor: '#16a34a' },
  DEBITO:      { label: 'Débito',      cor: '#dc2626' },
  COMPENSACAO: { label: 'Compensação', cor: '#7c3aed' },
  AJUSTE:      { label: 'Ajuste',      cor: '#d97706' },
  EXPIRACAO:   { label: 'Expiração',   cor: '#6b7280' },
}

function fmtS(s: number) {
  const neg = s < 0; const abs = Math.abs(s)
  return `${neg ? '-' : ''}${Math.floor(abs/3600)}h${String(Math.floor((abs%3600)/60)).padStart(2,'0')}`
}

export function BancoHorasPage() {
  const qc = useQueryClient()
  const { data: saldos, isLoading: loadSaldos } = useQuery({ queryKey: ['banco-horas-saldos'], queryFn: bancoHorasApi.listSaldos })
  const { data: funcs } = useQuery({ queryKey: ['funcionarios', true], queryFn: () => funcApi.list(true) })

  const [selectedFunc, setSelectedFunc] = useState<string | null>(null)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const { data: extrato, isLoading: loadExtrato } = useQuery({
    queryKey: ['banco-horas-extrato', selectedFunc, dataInicio, dataFim],
    queryFn: () => bancoHorasApi.extrato(selectedFunc!, dataInicio || undefined, dataFim || undefined),
    enabled: !!selectedFunc,
  })

  const [showLancar, setShowLancar] = useState(false)
  const [showAjuste, setShowAjuste] = useState(false)
  const [lancarForm, setLancarForm] = useState({ funcionario_id: '', tipo: 'CREDITO', horas: '', minutos: '', motivo: '', referencia_data: '', vencimento: '' })
  const [ajusteForm, setAjusteForm] = useState({ funcionario_id: '', horas: '', minutos: '', positivo: true, motivo: '' })
  const [modalError, setModalError] = useState<string | null>(null)

  const lancarMutation = useMutation({
    mutationFn: () => {
      const segundos = (Number(lancarForm.horas) * 3600) + (Number(lancarForm.minutos) * 60)
      return bancoHorasApi.lancar({
        funcionario_id: lancarForm.funcionario_id,
        tipo: lancarForm.tipo,
        segundos,
        motivo: lancarForm.motivo,
        referencia_data: lancarForm.referencia_data,
        vencimento: lancarForm.vencimento || undefined,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['banco-horas-saldos'] })
      qc.invalidateQueries({ queryKey: ['banco-horas-extrato'] })
      setShowLancar(false); setModalError(null)
    },
    onError: err => setModalError(getErrorMessage(err)),
  })

  const ajusteMutation = useMutation({
    mutationFn: () => {
      const abs_s = (Number(ajusteForm.horas) * 3600) + (Number(ajusteForm.minutos) * 60)
      const segundos = ajusteForm.positivo ? abs_s : -abs_s
      return bancoHorasApi.ajuste({ funcionario_id: ajusteForm.funcionario_id, segundos, motivo: ajusteForm.motivo })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['banco-horas-saldos'] })
      qc.invalidateQueries({ queryKey: ['banco-horas-extrato'] })
      setShowAjuste(false); setModalError(null)
    },
    onError: err => setModalError(getErrorMessage(err)),
  })

  const saldoSelecionado = saldos?.find((s: any) => s.funcionario_id === selectedFunc)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Banco de Horas</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Saldos, extrato e lançamentos — art. 59 §2° e §6° CLT
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button className="btn btn-ghost" onClick={() => { setModalError(null); setShowAjuste(true) }}>Ajuste manual</button>
          <button className="btn btn-primary" onClick={() => { setModalError(null); setShowLancar(true) }}>+ Lançamento</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 'var(--space-6)' }}>
        {/* Coluna esquerda: lista de saldos */}
        <div>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)', color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Saldos</div>
          {loadSaldos ? <PageLoading /> : !saldos?.length ? (
            <div className="card" style={{ padding: 'var(--space-6)', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-400)' }}>
              Nenhum saldo registrado.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {saldos.map((s: any) => {
                const pos = s.saldo_segundos >= 0
                return (
                  <div key={s.funcionario_id}
                    onClick={() => setSelectedFunc(s.funcionario_id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: 'var(--space-3) var(--space-4)', cursor: 'pointer',
                      borderBottom: '1px solid var(--color-gray-100)',
                      backgroundColor: selectedFunc === s.funcionario_id ? 'var(--color-primary-light, #eff6ff)' : 'transparent',
                    }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{s.funcionario_nome}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', color: pos ? '#16a34a' : '#dc2626' }}>
                      {fmtS(s.saldo_segundos)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Coluna direita: extrato */}
        <div>
          {!selectedFunc ? (
            <div className="card" style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--color-gray-400)' }}>
              Selecione um funcionário para ver o extrato.
            </div>
          ) : (
            <>
              {/* Saldo atual */}
              {saldoSelecionado && (
                <div className="card" style={{ marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)' }}>{saldoSelecionado.funcionario_nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
                      Atualizado: {saldoSelecionado.updated_at ? new Date(saldoSelecionado.updated_at).toLocaleDateString('pt-BR') : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', textAlign: 'right' }}>Saldo atual</div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-2xl)', color: saldoSelecionado.saldo_segundos >= 0 ? '#16a34a' : '#dc2626' }}>
                      {fmtS(saldoSelecionado.saldo_segundos)}
                    </div>
                  </div>
                </div>
              )}

              {/* Filtros de período */}
              <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', alignItems: 'center' }}>
                <input className="input" type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={{ maxWidth: 160 }} title="Data inicial" />
                <span style={{ color: 'var(--color-gray-400)' }}>até</span>
                <input className="input" type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 160 }} title="Data final" />
                <button className="btn btn-ghost" onClick={() => { setDataInicio(''); setDataFim('') }}>Limpar</button>
              </div>

              {/* Tabela de extrato */}
              {loadExtrato ? <PageLoading /> : !extrato?.length ? (
                <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
                  Nenhum lançamento encontrado.
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                        <th style={TH}>Data</th>
                        <th style={TH}>Tipo</th>
                        <th style={TH}>Horas</th>
                        <th style={TH}>Motivo</th>
                        <th style={TH}>Vencimento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extrato.map((l: any) => {
                        const ti = TIPO_LABELS[l.tipo] ?? { label: l.tipo, cor: '#6b7280' }
                        const isCred = l.tipo === 'CREDITO'
                        return (
                          <tr key={l.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <td style={TD}>{new Date(l.referencia_data + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                            <td style={TD}><span style={{ color: ti.cor, fontWeight: 600, fontSize: 'var(--font-size-xs)' }}>{ti.label}</span></td>
                            <td style={{ ...TD, fontWeight: 700, color: isCred ? '#16a34a' : '#dc2626' }}>
                              {isCred ? '+' : '-'}{fmtS(Math.abs(l.segundos))}
                            </td>
                            <td style={{ ...TD, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.motivo}</td>
                            <td style={{ ...TD, color: 'var(--color-gray-400)', fontSize: 'var(--font-size-xs)' }}>
                              {l.vencimento ? new Date(l.vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal: Lançamento */}
      {showLancar && (
        <Modal title="Novo Lançamento" onClose={() => setShowLancar(false)} error={modalError}>
          <F label="Funcionário">
            <select className="input" value={lancarForm.funcionario_id} onChange={e => setLancarForm(f => ({ ...f, funcionario_id: e.target.value }))}>
              <option value="">— Selecione —</option>
              {(funcs ?? []).map(f => <option key={f.id} value={f.id}>{f.nome} — {formatCpf(f.cpf)}</option>)}
            </select>
          </F>
          <F label="Tipo">
            <select className="input" value={lancarForm.tipo} onChange={e => setLancarForm(f => ({ ...f, tipo: e.target.value }))}>
              <option value="CREDITO">Crédito (horas extras a compensar)</option>
              <option value="DEBITO">Débito (compensação usada)</option>
              <option value="COMPENSACAO">Compensação em folha (pagamento)</option>
            </select>
          </F>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <F label="Horas"><input className="input" type="number" min={0} max={20} value={lancarForm.horas} onChange={e => setLancarForm(f => ({ ...f, horas: e.target.value }))} placeholder="0" /></F>
            <F label="Minutos"><input className="input" type="number" min={0} max={59} value={lancarForm.minutos} onChange={e => setLancarForm(f => ({ ...f, minutos: e.target.value }))} placeholder="0" /></F>
          </div>
          <F label="Data de referência"><input className="input" type="date" value={lancarForm.referencia_data} onChange={e => setLancarForm(f => ({ ...f, referencia_data: e.target.value }))} /></F>
          <F label="Motivo"><input className="input" value={lancarForm.motivo} onChange={e => setLancarForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Ex: Horas extras 26/06/2026" /></F>
          <F label="Vencimento do crédito" opt><input className="input" type="date" value={lancarForm.vencimento} onChange={e => setLancarForm(f => ({ ...f, vencimento: e.target.value }))} /></F>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <button className="btn btn-primary" onClick={() => lancarMutation.mutate()} disabled={lancarMutation.isPending}>
              {lancarMutation.isPending ? 'Lançando…' : 'Confirmar'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowLancar(false)}>Cancelar</button>
          </div>
        </Modal>
      )}

      {/* Modal: Ajuste Manual */}
      {showAjuste && (
        <Modal title="Ajuste Manual de Saldo" onClose={() => setShowAjuste(false)} error={modalError}>
          <div className="alert alert-warning" style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-xs)' }}>
            Ajuste manual será auditado. Use apenas para correções excepcionais.
          </div>
          <F label="Funcionário">
            <select className="input" value={ajusteForm.funcionario_id} onChange={e => setAjusteForm(f => ({ ...f, funcionario_id: e.target.value }))}>
              <option value="">— Selecione —</option>
              {(funcs ?? []).map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </F>
          <F label="Direção">
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input type="radio" checked={ajusteForm.positivo} onChange={() => setAjusteForm(f => ({ ...f, positivo: true }))} />
                <span style={{ color: '#16a34a', fontWeight: 600 }}>+ Crédito</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input type="radio" checked={!ajusteForm.positivo} onChange={() => setAjusteForm(f => ({ ...f, positivo: false }))} />
                <span style={{ color: '#dc2626', fontWeight: 600 }}>− Débito</span>
              </label>
            </div>
          </F>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <F label="Horas"><input className="input" type="number" min={0} max={100} value={ajusteForm.horas} onChange={e => setAjusteForm(f => ({ ...f, horas: e.target.value }))} placeholder="0" /></F>
            <F label="Minutos"><input className="input" type="number" min={0} max={59} value={ajusteForm.minutos} onChange={e => setAjusteForm(f => ({ ...f, minutos: e.target.value }))} placeholder="0" /></F>
          </div>
          <F label="Justificativa (obrigatória)"><input className="input" value={ajusteForm.motivo} onChange={e => setAjusteForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Descreva o motivo do ajuste" /></F>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <button className="btn btn-primary" onClick={() => ajusteMutation.mutate()} disabled={ajusteMutation.isPending}>
              {ajusteMutation.isPending ? 'Ajustando…' : 'Confirmar ajuste'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowAjuste(false)}>Cancelar</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

const TH: React.CSSProperties = { padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }
const TD: React.CSSProperties = { padding: 'var(--space-3) var(--space-4)', verticalAlign: 'middle' }

function Modal({ title, onClose, error, children }: { title: string; onClose: () => void; error: string | null; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 'var(--space-4)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card" style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{title}</h2>
        {error && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}
        {children}
      </div>
    </div>
  )
}

function F({ label, children, opt }: { label: string; children: React.ReactNode; opt?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{opt && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}
